import { useCallback, useEffect, useState } from 'react';
import { SlotInfo, View, Views } from 'react-big-calendar';

import { ITEMS_PER_PAGE } from 'modules/calendar/core/constant';
import { getListEventSessions } from 'modules/calendar/services';
import useEventStore from 'modules/calendar/store/event';
import { Classes } from 'modules/classes/core/type';

import { SelectedFilters } from 'components/filter-panel/type';

import { getDateRange } from 'helpers';
import { useBoolean } from 'hooks';
import { apiCall } from 'hooks/service';

export const useCalendarController = () => {
  const [view, setView] = useState<View>(Views.MONTH);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filters, setFilters] = useState<SelectedFilters>({});
  const [classesState, setClassesState] = useState<{
    classes: Classes[];
    page: number;
    hasMore: boolean;
    isLoading: boolean;
  }>({
    classes: [],
    page: 1,
    hasMore: true,
    isLoading: false,
  });
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});

  const setEvents = useEventStore((state) => state.setEvents);
  const events = useEventStore((state) => state.events);

  const drawer = useBoolean();

  const getListClasses = useCallback(async (pageNumber = 1, search = '') => {
    if (classesState.isLoading) return;

    setClassesState((prev) => ({ ...prev, isLoading: true }));

    try {
      const result: any = await apiCall({
        url: `/classes/compact-classes?page=${pageNumber}&limit=${ITEMS_PER_PAGE}${search ? `&search=${search}` : ''}`,
        method: 'GET',
        showError: true,
      });

      if (result)
        setClassesState((prev) => ({
          ...prev,
          classes: pageNumber === 1 ? result : [...prev.classes, ...result],
          hasMore: result.length === ITEMS_PER_PAGE,
          page: pageNumber,
        }));
    } finally {
      setClassesState((prev) => ({ ...prev, isLoading: false }));
    }
  }, []);

  useEffect(() => {
    getListClasses(1);
  }, [getListClasses]);

  const loadMoreClasses = useCallback(() => {
    if (!classesState.isLoading && classesState.hasMore)
      getListClasses(classesState.page + 1, searchQueries.classes || '');
  }, [
    classesState.isLoading,
    classesState.hasMore,
    classesState.page,
    getListClasses,
    searchQueries.classes,
  ]);

  useEffect(() => {
    const fetchEventSessions = async () => {
      const res = await getListEventSessions(getDateRange(view, currentDate));

      if (res) setEvents(res);
    };

    fetchEventSessions();
  }, [currentDate, view]);

  const handleSelectSlot = useCallback((slotInfo: SlotInfo) => {
    setCurrentDate(new Date(slotInfo.start));
    drawer.setValue(true);
  }, []);

  const handleDateChange = useCallback((_: object[], date: Date) => {
    setCurrentDate(date);
    setView(Views.DAY);
  }, []);

  const onNavigate = useCallback((newDate: Date) => setCurrentDate(newDate), []);

  const handleFilterSearch = useCallback(
    (filterKey: string, searchTerm: string) => {
      setSearchQueries((prev) => ({
        ...prev,
        [filterKey]: searchTerm,
      }));

      if (filterKey === 'classes') getListClasses(1, searchTerm);
    },
    [getListClasses, events],
  );

  return {
    view,
    currentDate,
    setCurrentDate,
    filters,
    setFilters,
    setView,
    handleSelectSlot,
    handleDateChange,
    onNavigate,
    drawer,
    classesState,
    loadMoreClasses,
    handleFilterSearch,
    searchQueries,
  };
};
